import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A WWI infantryman — a rig-compatible soldier figure built on the same
// skeleton and proportions as the standard actor (`worker-simple`), so every
// interaction and station program fits it unchanged. Its parts carry the actor
// rig names (`body`, `head`, `arm-l`, `arm-r`, `leg-l`, `leg-r`; the whole
// figure moves via the rig's `root`). Modelled facing +Z, feet on the floor.
//
// Look: khaki service tunic over puttee-wrapped trousers, a shallow-domed
// brodie helmet with a wide flat brim, a small canvas backpack with a bedroll,
// and a rifle slung diagonally across the back. The tunic and sleeves are
// tagged `slot: "team"`, so the khaki is only the default: a consumer
// recolours the uniform per side at build time (`--team`, the viewer's
// colour picker).
const KHAKI = "#7a6f4e";      // service tunic (the `team` slot's default)
const TROUSER = "#6c6244";    // trousers, a shade darker than the tunic
const PUTTEE = "#5c5138";     // wool puttee wraps
const BOOT = "#3a2e20";       // ammunition boots
const SKIN = "#d9a06b";
const STEEL = "#6e7266";      // brodie helmet
const CANVAS = "#cbbd93";     // pack webbing / bedroll
const PACK = "#8a7d5a";       // backpack body
const WOOD = "#6b4a2f";       // rifle stock
const GUNMETAL = "#4a4d52";   // rifle barrel
const BELT = "#4a3a28";       // leather belt

const LEG_H = 0.16;
const BODY_Y = LEG_H + 0.11;       // torso centre, stacked on the legs
const SHOULDER_Y = BODY_Y + 0.07;  // arm hinge height
const HEAD_Y = BODY_Y + 0.12 + 0.06;

// Animation pivots (model-local): neck, torso centre, shoulders, hips.
const E = "sine" as const;
const HP = [0, HEAD_Y - 0.05, 0] as const;
const BP = [0, BODY_Y, 0] as const;
const ALP = [-0.115, SHOULDER_Y, 0] as const;
const ARP = [0.115, SHOULDER_Y, 0] as const;
const LLP = [-0.05, LEG_H, 0] as const;
const LRP = [0.05, LEG_H, 0] as const;

// A trousered, puttee-wrapped leg ending in a boot.
function leg(name: string, x: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.07, 0.09, 0.08), color: TROUSER, position: [x, 0.115, 0], name },
    { geometry: new THREE.BoxGeometry(0.072, 0.07, 0.082), color: PUTTEE, position: [x, 0.055, 0], name },
    { geometry: new THREE.BoxGeometry(0.075, 0.04, 0.11), color: BOOT, position: [x, 0.02, 0.015], name },
  ];
}

// A tunic sleeve hung at the shoulder with a bare hand at the cuff.
function arm(name: string, x: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.045, 0.18, 0.05), color: KHAKI, position: [x, SHOULDER_Y - 0.07, 0], name, slot: "team" },
    { geometry: new THREE.BoxGeometry(0.04, 0.035, 0.045), color: SKIN, position: [x, SHOULDER_Y - 0.175, 0], name },
  ];
}

function infantrymanParts(): Part[] {
  return [
    ...leg("leg-l", -0.05),
    ...leg("leg-r", 0.05),
    // Khaki service tunic (the team-colour channel) over a leather belt.
    { geometry: new THREE.BoxGeometry(0.18, 0.22, 0.11), color: KHAKI, position: [0, BODY_Y, 0], name: "body", slot: "team" },
    { geometry: new THREE.BoxGeometry(0.185, 0.035, 0.115), color: BELT, position: [0, LEG_H + 0.03, 0], name: "body" },
    ...arm("arm-l", -0.115),
    ...arm("arm-r", 0.115),
    // Small canvas backpack with a bedroll strapped across the top.
    { geometry: new THREE.BoxGeometry(0.14, 0.13, 0.055), color: PACK, position: [0, BODY_Y + 0.03, -0.082], name: "body" },
    { geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.16, 6), color: CANVAS,
      position: [0, BODY_Y + 0.11, -0.085], rotation: [0, 0, Math.PI / 2], name: "body" },
    // Rifle slung diagonally across the back, muzzle over the left shoulder.
    { geometry: new THREE.BoxGeometry(0.028, 0.3, 0.026), color: WOOD,
      position: [0.02, BODY_Y + 0.02, -0.122], rotation: [0, 0, 0.35], name: "body" },
    { geometry: new THREE.CylinderGeometry(0.008, 0.008, 0.17, 5), color: GUNMETAL,
      position: [-0.062, BODY_Y + 0.185, -0.122], rotation: [0, 0, 0.35], name: "body" },
    // Head under the brodie helmet: shallow steel dome on a wide flat brim.
    { geometry: new THREE.BoxGeometry(0.11, 0.11, 0.11), color: SKIN, position: [0, HEAD_Y, 0], name: "head" },
    { geometry: new THREE.CylinderGeometry(0.095, 0.095, 0.014, 10), color: STEEL,
      position: [0, HEAD_Y + 0.045, 0], name: "head" },
    { geometry: new THREE.SphereGeometry(0.078, 10, 6), color: STEEL,
      position: [0, HEAD_Y + 0.048, 0], scale: [1, 0.55, 1], name: "head" },
  ];
}

export const infantryman = defineModel({
  id: "infantryman",
  batchId: "wwi",
  author: "Fable 5",
  name: "Infantryman",
  tags: ["wwi", "character", "actor", "humanoid", "person", "soldier", "military", "infantry"],
  summary: "A rig-compatible WWI soldier — khaki tunic, brodie helmet, backpack and a rifle slung on the back.",
  description:
    "The Great War rank-and-file figure: a rig-compatible actor on the standard " +
    "worker skeleton, in a khaki service tunic (the team-colour channel) with " +
    "puttee-wrapped trousers, a shallow brodie helmet, a small canvas backpack " +
    "with bedroll and a rifle slung diagonally across the back. Any interaction " +
    "or station program that poses the standard actor fits it unchanged.",
  footprint: 0.14,
  tiles: { w: 1, h: 1 },
  parts: infantrymanParts,
  // Where a carried good is cradled in front of the chest, matching the other
  // avatars; the good is supplied by the consumer at bake time.
  sockets: [
    { id: "carry", position: [0, 0.24, 0.13], slotType: "carry", accepts: ["carriable"],
      description: "A good carried in front of the chest." },
  ],
  animations: [
    { id: "idle", label: "Idle", duration: 2.0, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1, value: [0.02, 0, 0], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 1, value: [0, 0.006, 0], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1.3, value: [0, 0, -0.05], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 1.3, value: [0, 0, 0.05], ease: E }, { t: 2, value: [0, 0, 0], ease: E }] },
    ] },
    { id: "walk", label: "Walk", duration: 0.8, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0.04, 0, 0.02] }, { t: 0.4, value: [0.04, 0, -0.02], ease: E }, { t: 0.8, value: [0.04, 0, 0.02], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0.35, 0, 0] }, { t: 0.4, value: [-0.35, 0, 0], ease: E }, { t: 0.8, value: [0.35, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0, value: [-0.35, 0, 0] }, { t: 0.4, value: [0.35, 0, 0], ease: E }, { t: 0.8, value: [-0.35, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [-0.3, 0, 0] }, { t: 0.4, value: [0.3, 0, 0], ease: E }, { t: 0.8, value: [-0.3, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0.3, 0, 0] }, { t: 0.4, value: [-0.3, 0, 0], ease: E }, { t: 0.8, value: [0.3, 0, 0], ease: E }] },
      { target: "head", pivot: HP, position: [
        { t: 0, value: [0, 0, 0] }, { t: 0.2, value: [0, 0.006, 0], ease: E }, { t: 0.4, value: [0, 0, 0], ease: E },
        { t: 0.6, value: [0, 0.006, 0], ease: E }, { t: 0.8, value: [0, 0, 0], ease: E }] },
    ] },
    { id: "march", label: "March", duration: 1.0, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0.03] }, { t: 0.5, value: [0, 0, -0.03], ease: E }, { t: 1.0, value: [0, 0, 0.03], ease: E }] },
      { target: "leg-l", pivot: LLP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.25, value: [-0.6, 0, 0], ease: E }, { t: 0.5, value: [0, 0, 0], ease: E }] },
      { target: "leg-r", pivot: LRP, rotation: [
        { t: 0.5, value: [0, 0, 0] }, { t: 0.75, value: [-0.6, 0, 0], ease: E }, { t: 1.0, value: [0, 0, 0], ease: E }] },
      { target: "arm-l", pivot: ALP, rotation: [
        { t: 0, value: [0.35, 0, 0] }, { t: 0.5, value: [-0.35, 0, 0], ease: E }, { t: 1.0, value: [0.35, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [-0.35, 0, 0] }, { t: 0.5, value: [0.35, 0, 0], ease: E }, { t: 1.0, value: [-0.35, 0, 0], ease: E }] },
    ] },
    { id: "salute", label: "Salute", duration: 1.5, loop: false, tracks: [
      { target: "body", pivot: BP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-0.04, 0, 0], ease: E },
        { t: 1.1, value: [-0.04, 0, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "arm-r", pivot: ARP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [-1.8, 0.3, 0], ease: E },
        { t: 1.1, value: [-1.8, 0.3, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
      { target: "head", pivot: HP, rotation: [
        { t: 0, value: [0, 0, 0] }, { t: 0.3, value: [0.05, 0, 0], ease: E },
        { t: 1.1, value: [0.05, 0, 0] }, { t: 1.5, value: [0, 0, 0], ease: E }] },
    ] },
  ],
});
